import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { canSee, roleHomePath } from "@/lib/perms";
import { Toaster } from "@/components/ui/sonner";
import TableCardify from "@/components/app/TableCardify";
import { AppErrorBoundary, UpdateWatcher } from "@/components/app/Recovery";

import AppShell from "@/components/app/AppShell";
import Login from "@/pages/Login";
import PublicApply from "@/pages/PublicApply";
import InterviewRoom from "@/pages/InterviewRoom";
import ClearancePortal from "@/pages/ClearancePortal";
import AdjustmentRequests from "@/pages/AdjustmentRequests";
import EmployeeImport from "@/pages/EmployeeImport";
import DataMigration from "@/pages/DataMigration";
import BiometricDevices from "@/pages/BiometricDevices";
import SSOSettings from "@/pages/SSOSettings";
import ApiAccess from "@/pages/ApiAccess";
import Offboarding2 from "@/pages/Offboarding";
import AIInterview from "@/pages/AIInterview";
import PublicCareers from "@/pages/PublicCareers";
import Dashboard from "@/pages/Dashboard";
import Employees from "@/pages/Employees";
import EmployeeDetail from "@/pages/EmployeeDetail";
import Organization from "@/pages/Organization";
import Attendance from "@/pages/Attendance";
import Shifts from "@/pages/Shifts";
import { LeavesPage, OvertimePage } from "@/pages/Approvals";
import ClockIn from "@/pages/ClockIn";
import PayrollRuns from "@/pages/PayrollRuns";
import PayrollRunDetail from "@/pages/PayrollRunDetail";
import PayrollControlCenter from "@/pages/PayrollControlCenter";
import FinalPay from "@/pages/FinalPay";
import ComplianceReports from "@/pages/ComplianceReports";
import ComplianceReadiness from "@/pages/ComplianceReadiness";
import ContributionTables from "@/pages/ContributionTables";
import ReportsCenter from "@/pages/ReportsCenter";
import PayrollSettings from "@/pages/PayrollSettings";
import Disbursement from "@/pages/Disbursement";
import Bonuses from "@/pages/Bonuses";
import Learning from "@/pages/Learning";
import MyLearning from "@/pages/MyLearning";
import Engagement from "@/pages/Engagement";
import Rewards from "@/pages/Rewards";
import RewardsExchange from "@/pages/RewardsExchange";
import PeopleAnalytics from "@/pages/PeopleAnalytics";
import OwnerDashboard from "@/pages/OwnerDashboard";
import RolesPermissions from "@/pages/RolesPermissions";
import Holidays from "@/pages/Holidays";
import ManualPage from "@/pages/ManualPage";
import Loans from "@/pages/Loans";
import MyRequests from "@/pages/MyRequests";
import Notifications from "@/pages/Notifications";
import DocumentRequests from "@/pages/DocumentRequests";
import Contracts from "@/pages/Contracts";
import Recruitment from "@/pages/Recruitment";
import Onboarding from "@/pages/Onboarding";
import { OnboardingPage, TrainingPage, RelationsPage, HelpdeskPage, BenefitsPage, AssetsPage } from "@/pages/Foundations";
import Performance from "@/pages/Performance";
import ApprovalsInbox from "@/pages/ApprovalsInbox";
import Announcements from "@/pages/Announcements";
import { UsersPage, CompaniesPage, AuditPage, MinimumWagePage, LeavePoliciesPage } from "@/pages/Admin";
import Automation from "@/pages/Automation";
import AIAssistant from "@/pages/AIAssistant";
import AIAdmin from "@/pages/AIAdmin";
import MyDashboard from "@/pages/MyDashboard";
import MyProfile from "@/pages/MyProfile";
import MyTime from "@/pages/MyTime";
import MyPayslips from "@/pages/MyPayslips";
import MyTickets from "@/pages/MyTickets";
import TeamDashboard from "@/pages/TeamDashboard";

function Protected({ children }) {
    const { user, loading } = useAuth();
    if (loading) return <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">Loading…</div>;
    if (!user) return <Navigate to="/login" replace />;
    return children;
}

function Gate({ permKey, children }) {
    const { user, activeCompanyId } = useAuth();
    if (!canSee(user, activeCompanyId, permKey)) {
        return <Navigate to={roleHomePath(user, activeCompanyId)} replace />;
    }
    return children;
}

function HomeRedirect() {
    const { user, activeCompanyId } = useAuth();
    return <Navigate to={roleHomePath(user, activeCompanyId)} replace />;
}

function CrashTest() {
    throw new Error("Deliberate test crash (/debug/crash)");
}

function App() {
    return (
        <BrowserRouter>
            <AuthProvider>
                <Toaster richColors closeButton position="top-right" />
                <TableCardify />
                <UpdateWatcher />
                <AppErrorBoundary>
                <Routes>
                    <Route path="/login" element={<Login />} />
                    {/* Public careers — unauthenticated. Static /careers/company/:x
                        (2 segments) ranks above the single-segment job route. */}
                    <Route path="/careers/company/:companyToken" element={<PublicCareers />} />
                    <Route path="/careers/:token" element={<PublicApply />} />
                    <Route path="/interview/:token" element={<InterviewRoom />} />
                    <Route path="/clearance/:token" element={<ClearancePortal />} />
                    <Route path="/" element={<Protected><AppShell /></Protected>}>
                        <Route index element={<HomeRedirect />} />
                        {/* Self-service (all authenticated) */}
                        <Route path="me" element={<Gate permKey="my_dashboard"><MyDashboard /></Gate>} />
                        <Route path="me/profile" element={<Gate permKey="my_profile"><MyProfile /></Gate>} />
                        <Route path="me/clock-in" element={<Gate permKey="clock_in"><ClockIn /></Gate>} />
                        <Route path="me/time" element={<Gate permKey="my_time"><MyTime /></Gate>} />
                        <Route path="me/payslips" element={<Gate permKey="my_payslips"><MyPayslips /></Gate>} />
                        <Route path="me/tickets" element={<Gate permKey="my_tickets"><MyTickets /></Gate>} />
                        <Route path="me/learning" element={<Gate permKey="my_learning"><MyLearning /></Gate>} />
                        <Route path="me/requests" element={<Gate permKey="my_requests"><MyRequests /></Gate>} />
                        <Route path="me/notifications" element={<Gate permKey="notifications"><Notifications /></Gate>} />
                        {/* Team (managers + HR) */}
                        <Route path="team" element={<Gate permKey="team"><TeamDashboard /></Gate>} />
                        {/* Executive dashboard */}
                        <Route path="dashboard" element={<Gate permKey="dashboard"><Dashboard /></Gate>} />
                        <Route path="analytics" element={<Gate permKey="people_analytics"><PeopleAnalytics /></Gate>} />
                        <Route path="owner" element={<Gate permKey="owner_overview"><OwnerDashboard /></Gate>} />
                        <Route path="hris">
                            <Route path="employees" element={<Gate permKey="employees"><Employees /></Gate>} />
                            <Route path="employees/:id" element={<Gate permKey="employees"><EmployeeDetail /></Gate>} />
                            <Route path="organization" element={<Gate permKey="organization"><Organization /></Gate>} />
                            <Route path="document-requests" element={<Gate permKey="document_requests"><DocumentRequests /></Gate>} />
                            <Route path="contracts" element={<Gate permKey="contracts"><Contracts /></Gate>} />
                            <Route path="import" element={<Gate permKey="employee_import"><EmployeeImport /></Gate>} />
                            <Route path="migration" element={<Gate permKey="data_migration"><DataMigration /></Gate>} />
                        </Route>
                        <Route path="timekeeping">
                            <Route path="clock-in" element={<Navigate to="/me/clock-in" replace />} />
                            <Route path="attendance" element={<Gate permKey="attendance"><Attendance /></Gate>} />
                            <Route path="shifts" element={<Gate permKey="shifts"><Shifts /></Gate>} />
                            <Route path="devices" element={<Gate permKey="biometric_devices"><BiometricDevices /></Gate>} />
                            <Route path="holidays" element={<Gate permKey="holidays_admin"><Holidays /></Gate>} />
                            <Route path="leaves" element={<Gate permKey="leaves"><LeavesPage /></Gate>} />
                            <Route path="overtime" element={<Gate permKey="overtime"><OvertimePage /></Gate>} />
                            <Route path="adjustments" element={<Gate permKey="adjustments"><AdjustmentRequests /></Gate>} />
                        </Route>
                        <Route path="payroll">
                            <Route path="runs" element={<Gate permKey="payroll_runs"><PayrollRuns /></Gate>} />
                            <Route path="runs/:id" element={<Gate permKey="payroll_runs"><PayrollRunDetail /></Gate>} />
                            <Route path="control" element={<Gate permKey="payroll_control"><PayrollControlCenter /></Gate>} />
                            <Route path="final-pay" element={<Gate permKey="final_pay"><FinalPay /></Gate>} />
                            <Route path="disbursement" element={<Gate permKey="disbursement"><Disbursement /></Gate>} />
                            <Route path="loans" element={<Gate permKey="payroll_loans"><Loans /></Gate>} />
                            <Route path="reports" element={<Gate permKey="reports_center"><ReportsCenter /></Gate>} />
                            <Route path="bonuses" element={<Gate permKey="bonuses"><Bonuses /></Gate>} />
                            <Route path="settings" element={<Gate permKey="payroll_settings"><PayrollSettings /></Gate>} />
                        </Route>
                        <Route path="compliance">
                            <Route path="readiness" element={<Gate permKey="compliance_readiness"><ComplianceReadiness /></Gate>} />
                            <Route path="reports" element={<Gate permKey="compliance_reports"><ComplianceReports /></Gate>} />
                            {/* Contribution tables moved into Payroll Settings (maker-checker). */}
                            <Route path="tables" element={<Navigate to="/payroll/settings" replace />} />
                        </Route>
                        <Route path="recruitment" element={<Gate permKey="recruitment"><Recruitment /></Gate>} />
                        <Route path="recruitment/ai-interview" element={<Gate permKey="recruitment"><AIInterview /></Gate>} />
                        <Route path="onboarding" element={<Gate permKey="onboarding"><Onboarding /></Gate>} />
                        <Route path="offboarding" element={<Gate permKey="offboarding"><Offboarding2 /></Gate>} />
                        <Route path="performance" element={<Gate permKey="performance"><Performance /></Gate>} />
                        <Route path="training" element={<Gate permKey="training"><TrainingPage /></Gate>} />
                        <Route path="learning" element={<Gate permKey="learning"><Learning /></Gate>} />
                        <Route path="relations" element={<Gate permKey="relations"><RelationsPage /></Gate>} />
                        <Route path="helpdesk" element={<Gate permKey="helpdesk"><HelpdeskPage /></Gate>} />
                        <Route path="benefits" element={<Gate permKey="benefits"><BenefitsPage /></Gate>} />
                        <Route path="assets" element={<Gate permKey="assets"><AssetsPage /></Gate>} />
                        <Route path="approvals" element={<Gate permKey="approvals_inbox"><ApprovalsInbox /></Gate>} />
                        <Route path="announcements" element={<Gate permKey="announcements"><Announcements /></Gate>} />
                        <Route path="engagement" element={<Gate permKey="engagement"><Engagement /></Gate>} />
                        <Route path="rewards" element={<Gate permKey="rewards"><Rewards /></Gate>} />
                        <Route path="rewards/exchange" element={<Gate permKey="rewards"><RewardsExchange /></Gate>} />
                        <Route path="ai/assistant" element={<Gate permKey="ai_assistant"><AIAssistant /></Gate>} />
                        <Route path="manual" element={<Gate permKey="user_manual"><ManualPage /></Gate>} />
                        <Route path="admin">
                            <Route path="users" element={<Gate permKey="admin_users"><UsersPage /></Gate>} />
                            <Route path="rbac" element={<Gate permKey="admin_rbac"><RolesPermissions /></Gate>} />
                            <Route path="companies" element={<Gate permKey="admin_companies"><CompaniesPage /></Gate>} />
                            <Route path="audit" element={<Gate permKey="admin_audit"><AuditPage /></Gate>} />
                            <Route path="automation" element={<Gate permKey="admin_automation"><Automation /></Gate>} />
                            {/* Minimum wage moved into Payroll Settings (maker-checker). */}
                            <Route path="minimum-wages" element={<Navigate to="/payroll/settings" replace />} />
                            <Route path="leave-policies" element={<Gate permKey="admin_leave_policies"><LeavePoliciesPage /></Gate>} />
                            <Route path="ai" element={<Gate permKey="ai_admin"><AIAdmin /></Gate>} />
                            <Route path="sso" element={<Gate permKey="sso"><SSOSettings /></Gate>} />
                            <Route path="api" element={<Gate permKey="api_access"><ApiAccess /></Gate>} />
                        </Route>
                    </Route>
                    {/* Proves the error boundary works (recovery card, not a white screen). */}
                    <Route path="debug/crash" element={<CrashTest />} />
                    <Route path="*" element={<HomeRedirect />} />
                </Routes>
                </AppErrorBoundary>
            </AuthProvider>
        </BrowserRouter>
    );
}

export default App;
